import React from 'react';
import PropTypes from 'prop-types';
import { notifySuccess, notifyWarning } from '../../services/notificationService';

/**
 * QuestionCard - Individual question display with edit/delete/reroll actions
 */
function QuestionCard({
  question,
  index,
  isEditing,
  editedQuestion,
  setEditedQuestion,
  errors,
  isGenerating,
  getQualityScore,
  onReroll,
  onGenerateVariation,
  onToggleMultipleChoice,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onRefine
}) {
  const q = question;
  
  if (isEditing) {
    // EDIT MODE
    return (
      <div className={`p-4 rounded border ${errors ? 'bg-danger-50 dark:bg-danger-900/30 border-danger-300 dark:border-danger-700' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-700'}`}>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Frage:</label>
            <input
              type="text"
              value={editedQuestion.question}
              onChange={(e) => setEditedQuestion({...editedQuestion, question: e.target.value})}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Antwortoptionen:</label>
            {editedQuestion.options.map((opt, optIdx) => (
              <div key={optIdx} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => {
                    const newOptions = [...editedQuestion.options];
                    newOptions[optIdx] = e.target.value;
                    setEditedQuestion({...editedQuestion, options: newOptions});
                  }}
                  className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <button
                  onClick={() => {
                    const newOptions = editedQuestion.options.filter((_, i) => i !== optIdx);
                    setEditedQuestion({...editedQuestion, options: newOptions});
                  }}
                  className="text-danger-600 dark:text-danger-400 hover:text-danger-800 dark:hover:text-danger-300 px-2"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                setEditedQuestion({
                  ...editedQuestion, 
                  options: [...editedQuestion.options, '']
                });
              }}
              className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300"
            >
              + Option hinzufügen
            </button>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Korrekte Antwort{editedQuestion.allowMultiple ? 'en' : ''}:
            </label>
            {editedQuestion.allowMultiple ? (
              /* Multiple Choice: Checkboxes */
              <div className="space-y-2">
                {editedQuestion.options.map((opt, idx) => (
                  <label key={idx} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editedQuestion.correctAnswerIndices?.includes(idx) ?? false}
                      onChange={(e) => {
                        const currentIndices = editedQuestion.correctAnswerIndices || [];
                        const newIndices = e.target.checked
                          ? [...currentIndices, idx].sort((a, b) => a - b)
                          : currentIndices.filter(i => i !== idx);
                        setEditedQuestion({...editedQuestion, correctAnswerIndices: newIndices});
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {String.fromCharCode(65 + idx)}. {opt.substring(0, 30)}...
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              /* Single Choice: Select */
              <select
                value={editedQuestion.correctAnswerIndices?.[0] ?? 0}
                onChange={(e) => setEditedQuestion({...editedQuestion, correctAnswerIndices: [parseInt(e.target.value)]})}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {editedQuestion.options.map((opt, idx) => (
                  <option key={idx} value={idx}>
                    {String.fromCharCode(65 + idx)}. {opt.substring(0, 30)}...
                  </option>
                ))}
              </select>
            )}
          </div>
          
          <div className="flex gap-2 pt-2">
            <button
              onClick={onSaveEdit}
              className="px-4 py-2 bg-success-600 text-white rounded hover:bg-success-700"
            >
              ✓ Speichern
            </button>
            <button
              onClick={onCancelEdit}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // VIEW MODE
  const quality = getQualityScore(q);
  
  return (
    <div className={`p-4 rounded border ${errors ? 'bg-danger-50 dark:bg-danger-900/30 border-danger-300 dark:border-danger-700' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-700'}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <div className="font-medium mb-1 text-gray-900 dark:text-white">
            {index + 1}. {q.question}
          </div>
          <div className="flex items-center gap-2">
            <div className={`text-xs px-2 py-0.5 rounded ${
              quality.score >= 90 ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300' :
              quality.score >= 70 ? 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300' :
              'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300'
            }`}>
              Qualität: {quality.score}%
            </div>
            {quality.issues.length > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400" title={quality.issues.join(', ')}>
                ⚠️ {quality.issues.length} Hinweis(e)
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 ml-2">
          <button
            onClick={() => onReroll(index)}
            disabled={isGenerating}
            className="text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 text-sm px-2 py-1 border border-orange-300 dark:border-orange-600 rounded hover:bg-orange-50 dark:hover:bg-orange-900/30 disabled:opacity-50"
            title="Frage komplett neu generieren"
          >
            🎲
          </button>
          <button
            onClick={() => onGenerateVariation(index)}
            disabled={isGenerating}
            className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-300 text-sm px-2 py-1 border border-secondary-300 dark:border-secondary-600 rounded hover:bg-secondary-50 dark:hover:bg-secondary-900/30 disabled:opacity-50"
            title="Ähnliche Variation generieren"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {onRefine && (
            <button
              onClick={() => onRefine(index)}
              disabled={isGenerating}
              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 text-sm px-2 py-1 border border-purple-300 dark:border-purple-600 rounded hover:bg-purple-50 dark:hover:bg-purple-900/30 disabled:opacity-50"
              title="Frage im Chat verfeinern"
            >
              💬
            </button>
          )}
          <button
            onClick={() => onToggleMultipleChoice(index)}
            className={`text-sm px-2 py-1 border rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/30 ${
              q.allowMultiple 
                ? 'text-indigo-600 dark:text-indigo-400 border-indigo-300 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30' 
                : 'text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600'
            }`}
            title={q.allowMultiple ? 'Zu Single Choice ändern' : 'Zu Multiple Choice ändern'}
          >
            {q.allowMultiple ? '☑️' : '⭕'}
          </button>
          <button
            onClick={() => onStartEditing(index)}
            className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 text-sm px-2 py-1 border border-primary-300 dark:border-primary-600 rounded hover:bg-primary-50 dark:hover:bg-primary-900/30"
            title="Frage bearbeiten"
          >
            ✏️
          </button>
          <button
            onClick={() => onDelete(index)}
            className="text-danger-600 dark:text-danger-400 hover:text-danger-800 dark:hover:text-danger-300 text-sm px-2 py-1 border border-danger-300 dark:border-danger-600 rounded hover:bg-danger-50 dark:hover:bg-danger-900/30"
            title="Frage löschen"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
      
      {errors && (
        <div className="mb-2 text-xs text-danger-700 dark:text-danger-300 bg-danger-100 dark:bg-danger-900/30 p-2 rounded">
          ⚠️ {errors.join(', ')}
        </div>
      )}
      
      {q.codeBlock && q.codeBlock.code && (
        <div className="mb-2 text-xs bg-gray-900 text-gray-100 p-2 rounded">
          <div className="text-gray-400 mb-1">{q.codeBlock.language}</div>
          <pre className="overflow-x-auto">{q.codeBlock.code}</pre>
        </div>
      )}

      {q.terminalOutput && (
        <div className="mb-2 text-xs bg-black text-success-400 p-2 rounded border border-success-600">
          <div className="text-success-300 mb-1">Terminal Output:</div>
          <pre className="overflow-x-auto">{q.terminalOutput}</pre>
        </div>
      )}
      
      <ol className="text-sm space-y-1">
        {q.options.map((opt, optIndex) => {
          // Always use correctAnswerIndices (the standard format)
          const isCorrect = q.correctAnswerIndices?.includes(optIndex) ?? optIndex === 0;
          
          return (
            <li
              key={optIndex}
              className={isCorrect ? 'text-success-700 dark:text-success-400 font-medium' : 'text-gray-700 dark:text-gray-300'}
            >
              {String.fromCharCode(65 + optIndex)}. {opt}
              {isCorrect && ' ✓'}
            </li>
          );
        })}
      </ol>
      
      {q.allowMultiple && (
        <div className="mt-2 text-xs text-secondary-600 dark:text-secondary-400 font-medium">
          ⚡ Multiple Choice - {q.correctAnswerIndices?.length || 0} richtige Antworten
        </div>
      )}
    </div>
  );
}

/**
 * GeneratedQuestionsPreview - Preview and manage generated questions
 */
function GeneratedQuestionsPreview({
  questions,
  setQuestions,
  validationErrors,
  editingIndex,
  editedQuestion,
  setEditedQuestion,
  isGenerating,
  selectedSession,
  // Functions
  getQualityScore,
  findDuplicates,
  validateAllQuestions,
  onImportQuestions,
  onRerollQuestion,
  onGenerateVariation,
  onToggleMultipleChoice,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onSaveToSession,
  onDownloadQuestions,
  onRefineQuestion
}) {
  
  const handleDelete = (index) => {
    const newQuestions = questions.filter((_, i) => i !== index);
    setQuestions(newQuestions);
    validateAllQuestions(newQuestions);
  };

  const handleCheckDuplicates = () => {
    const duplicates = findDuplicates();
    if (duplicates.length === 0) {
      notifySuccess('Keine Duplikate gefunden!');
    } else {
      const message = duplicates.map(d => 
        `Frage ${d.index1 + 1} und ${d.index2 + 1}: ${Math.round(d.similarity * 100)}% ähnlich`
      ).join(', ');
      notifyWarning(`${duplicates.length} mögliche Duplikate: ${message}`);
    }
  };

  const handleValidate = () => {
    const isValid = validateAllQuestions();
    if (isValid) {
      notifySuccess('Alle Fragen sind gültig!');
    } else {
      notifyWarning(`${Object.keys(validationErrors).length} Fragen haben Fehler`);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          ✅ Generierte Fragen ({questions.length})
        </h2>
        <div className="flex gap-2">
          <button
            onClick={handleCheckDuplicates}
            className="text-sm px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded hover:bg-orange-200 dark:hover:bg-orange-800"
          >
            🔍 Duplikate
          </button>
          <button
            onClick={handleValidate}
            className="text-sm px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded hover:bg-primary-200 dark:hover:bg-primary-800"
          >
            <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Validieren
          </button>
          <label className="text-sm px-3 py-1 bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300 rounded hover:bg-success-200 dark:hover:bg-success-800 cursor-pointer flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Importieren
            <input
              type="file"
              accept=".json"
              onChange={onImportQuestions}
              className="hidden"
            />
          </label>
        </div>
      </div>
      
      {/* Question List */}
      <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
        {questions.map((q, index) => (
          <QuestionCard
            key={index}
            question={q}
            index={index}
            isEditing={editingIndex === index}
            editedQuestion={editedQuestion}
            setEditedQuestion={setEditedQuestion}
            errors={validationErrors[index]}
            isGenerating={isGenerating}
            getQualityScore={getQualityScore}
            onReroll={onRerollQuestion}
            onGenerateVariation={onGenerateVariation}
            onToggleMultipleChoice={onToggleMultipleChoice}
            onStartEditing={onStartEditing}
            onSaveEdit={onSaveEdit}
            onCancelEdit={onCancelEdit}
            onDelete={handleDelete}
            onRefine={onRefineQuestion}
          />
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={onSaveToSession}
          disabled={!selectedSession}
          className={`flex-1 py-2 px-4 rounded font-medium ${
            !selectedSession
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-success-600 text-white hover:bg-success-700'
          }`}
        >
          <svg className="w-5 h-5 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
          </svg>
          Zu Quiz hinzufügen
        </button>
        
        <button
          onClick={onDownloadQuestions}
          className="flex-1 bg-primary-600 text-white py-2 px-4 rounded font-medium hover:bg-primary-700 flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Als JSON herunterladen
        </button>
        
        <button
          onClick={() => setQuestions([])}
          className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

GeneratedQuestionsPreview.propTypes = {
  questions: PropTypes.array.isRequired,
  setQuestions: PropTypes.func.isRequired,
  validationErrors: PropTypes.object.isRequired,
  editingIndex: PropTypes.number,
  editedQuestion: PropTypes.object,
  setEditedQuestion: PropTypes.func.isRequired,
  isGenerating: PropTypes.bool.isRequired,
  selectedSession: PropTypes.string,
  getQualityScore: PropTypes.func.isRequired,
  findDuplicates: PropTypes.func.isRequired,
  validateAllQuestions: PropTypes.func.isRequired,
  onImportQuestions: PropTypes.func.isRequired,
  onRerollQuestion: PropTypes.func.isRequired,
  onGenerateVariation: PropTypes.func.isRequired,
  onToggleMultipleChoice: PropTypes.func.isRequired,
  onStartEditing: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onSaveToSession: PropTypes.func.isRequired,
  onDownloadQuestions: PropTypes.func.isRequired,
  onRefineQuestion: PropTypes.func // Optional - opens refinement chat
};

export default GeneratedQuestionsPreview;
